"use client";

import { createContext, useCallback, useContext, useState } from "react";

type ToastVariant = "success" | "error" | "info";

type Toast = {
  id: number;
  message: string;
  variant: ToastVariant;
};

type AdminToastContextValue = {
  toast: (message: string, variant?: ToastVariant) => void;
};

const AdminToastCtx = createContext<AdminToastContextValue>({
  toast: () => {},
});

let nextId = 0;

export function AdminToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, variant: ToastVariant = "success") => {
    const id = ++nextId;
    setToasts((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  return (
    <AdminToastCtx.Provider value={{ toast }}>
      {children}
      {/* Toast container — fixed bottom-right, above modals */}
      {toasts.length > 0 && (
        <div className="fixed bottom-6 right-6 z-[600] flex flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`animate-in slide-in-from-right fade-in flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
                t.variant === "success"
                  ? "bg-success text-white"
                  : t.variant === "error"
                    ? "bg-error text-white"
                    : "bg-wildroost-ink text-white"
              }`}
            >
              {t.variant === "success" && (
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {t.variant === "error" && (
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              {t.message}
              <button
                type="button"
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                className="ml-2 shrink-0 rounded p-0.5 text-white/60 hover:text-white"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </AdminToastCtx.Provider>
  );
}

export function useAdminToast() {
  return useContext(AdminToastCtx);
}
